// Care records — the patient-facing, non-clinical view. We show only the thin Class-B visit recaps
// (visitSummaries) the platform is allowed to surface; the full medical record (visit notes and
// results) lives ONLY in the clinician's secure chart (D5), never persisted or rendered here.
// Patients do NOT open the Healthie chart from here (per FB) — the record stays with the care team.
//
// A pill tab group (All / Visit recaps / Prescriptions) filters the two real record sources we have.
// We intentionally do NOT mirror the mockup's "Labs" / full "Visit notes" tabs: that data lives only
// in Healthie (D5) and would render empty/misleading here. Both sections stay mounted and are toggled
// by visibility so switching tabs is instant (no refetch flash).
import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { formatDateTime } from "../lib/format";
import { Loading, ErrorState } from "../components/ui";
import { Prescriptions } from "../components/Prescriptions";

const NoteIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

type Tab = "all" | "visit" | "prescriptions";

export function Care() {
  const api = useApi();
  const { data, loading, error } = useAsync(() => api.visitSummaries(), []);
  const [tab, setTab] = useState<Tab>("all");

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} />;

  const recaps = data ?? [];
  const showRecaps = tab === "all" || tab === "visit";
  const showPrescriptions = tab === "all" || tab === "prescriptions";

  return (
    <>
      <div className="app-page-head">
        <h1>Health <em>records.</em></h1>
        <p>Your visit recaps and prescriptions from your care team.</p>
      </div>

      <div className="tab-pills" role="tablist" aria-label="Record type">
        <button className={`tab-pill ${tab === "all" ? "active" : ""}`} onClick={() => setTab("all")} role="tab" aria-selected={tab === "all"}>All</button>
        <button className={`tab-pill ${tab === "visit" ? "active" : ""}`} onClick={() => setTab("visit")} role="tab" aria-selected={tab === "visit"}>Visit recaps</button>
        <button className={`tab-pill ${tab === "prescriptions" ? "active" : ""}`} onClick={() => setTab("prescriptions")} role="tab" aria-selected={tab === "prescriptions"}>Prescriptions</button>
      </div>

      {/* Thin visit recaps as record cards (real, Class-B) */}
      <div style={{ display: showRecaps ? undefined : "none" }}>
        <div className="app-card-head" style={{ marginTop: 8 }}>
          <h3>Visit recaps</h3>
          <Link to="/visits">All visits →</Link>
        </div>
        {recaps.length ? (
          <div className="records-grid">
            {recaps.map((s) => (
              <div className="record-card" key={s.id}>
                <div className="record-card-head">
                  <div className="record-card-icon">{NoteIcon}</div>
                  <span className="record-tag normal">Visit recap</span>
                </div>
                <h4>Visit summary</h4>
                <div className="meta">{formatDateTime(s.last_visit_at)}</div>
                <p>{s.summary_text}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="pt-empty">No visit recaps yet.</div>
        )}
      </div>

      {/* Prescriptions written by the care team (read-only, own-records-only) */}
      <div style={{ display: showPrescriptions ? undefined : "none" }}>
        <Prescriptions />
      </div>
    </>
  );
}
